import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/Dashboard';
import GameSetup from './pages/GameSetup';
import Game from './pages/Game';
import GameOver from './pages/GameOver';
import JoinGame from './pages/JoinGame';
import Profile from './pages/Profile';
import Leaderboards from './pages/Leaderboards';
import Achievements from './pages/Achievements';
import GameHistory from './pages/GameHistory';
import HistoricalGameView from './pages/HistoricalGameView';
import OfflineNotice from './components/OfflineNotice';

// Legacy game pages (for direct access)
import FiveCrownsScorecard from './pages/FiveCrowns';
import Phase10Scorecard from './pages/Phase10';

// TV Display pages (for AirPlay/casting)
import FiveCrownsTVDisplay from './pages/FiveCrownsTVDisplay';
import Phase10TVDisplay from './pages/Phase10TVDisplay';
import GameTVDisplay from './pages/GameTVDisplay';

// API configuration
const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://games-api.jaydenmetz.com';

// Routes that render entirely from localStorage and never call the backend
const OFFLINE_ROUTES = ['/five-crowns', '/phase-10', '/five-crowns/tv', '/phase-10/tv'];

// Storage helper for localStorage with optional API sync
window.storage = {
  async get(key, local = true) {
    if (local) {
      const value = localStorage.getItem(key);
      return value ? { value } : null;
    }
    return null;
  },
  async set(key, value, local = true) {
    if (local) {
      localStorage.setItem(key, value);
      return true;
    }
    return false;
  }
};

// Export API URL for use in other components
window.API_URL = API_URL;

function AppRoutes() {
  const { user, loading, needsOnboarding } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect to dashboard when user is fully authenticated
  useEffect(() => {
    if (user && !needsOnboarding && location.pathname === '/') {
      // Already on dashboard, do nothing
    } else if (user && !needsOnboarding && (location.pathname === '/login' || location.pathname === '/onboarding')) {
      navigate('/', { replace: true });
    }
  }, [user, needsOnboarding, location.pathname, navigate]);

  // The standalone scorecards talk to no backend at all, so they stay reachable
  // ahead of the auth gate. Logging in requires the API, and without this an
  // outage locks you out of the one part of the app that never needed it.
  if (OFFLINE_ROUTES.includes(location.pathname)) {
    return (
      <Routes>
        <Route path="/five-crowns" element={<FiveCrownsScorecard />} />
        <Route path="/phase-10" element={<Phase10Scorecard />} />
        <Route path="/five-crowns/tv" element={<FiveCrownsTVDisplay />} />
        <Route path="/phase-10/tv" element={<Phase10TVDisplay />} />
      </Routes>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <p className="text-white">Loading...</p>
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return <Login />;
  }

  // Needs to complete onboarding (password reset, phone verify, email verify)
  if (needsOnboarding) {
    return <Onboarding />;
  }

  // Fully authenticated
  return (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/game-setup/:gameSlug" element={<GameSetup />} />
      <Route path="/join/:joinCode" element={<JoinGame />} />
      <Route path="/game/:activeGameId" element={<Game />} />
      <Route path="/game-over" element={<GameOver />} />
      <Route path="/profile/:userId" element={<Profile />} />
      <Route path="/leaderboards" element={<Leaderboards />} />
      <Route path="/achievements" element={<Achievements />} />
      <Route path="/history/:sessionId" element={<GameHistory />} />
      <Route path="/history/:sessionId/edit" element={<HistoricalGameView />} />

      {/* Legacy direct game access */}
      <Route path="/five-crowns" element={<FiveCrownsScorecard />} />
      <Route path="/phase-10" element={<Phase10Scorecard />} />

      {/* TV Display modes */}
      <Route path="/five-crowns/tv" element={<FiveCrownsTVDisplay />} />
      <Route path="/phase-10/tv" element={<Phase10TVDisplay />} />
      <Route path="/game/:activeGameId/tv" element={<GameTVDisplay />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        {/* Sits outside AppRoutes so it still shows on the login screen, which
            is exactly where you land when the API is down. */}
        <OfflineNotice />
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
